import { ColorPicker, Link, PlanBadge, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { useTranslation } from '@hotjar/translations';
import { WizardLabel } from '@hotjar/feedback/components/common/Wizard/WizardLabel';

const Wrapper = styled.div`
  margin-bottom: ${tokens.spacer.x2};
`;

const Caption = styled.div`
  ${tokens.text('caption')}
  color: ${tokens.color.text_light};
  margin-bottom: ${tokens.spacer.x1};
`;

const PickerWrapper = styled.div`
  display: grid;
  grid-template-columns: 146px min-content;
`;

const PickerAction = styled.div`
  display: flex;
  align-items: center;
  padding: 0 ${tokens.spacer.x2};
`;

const ResetButton = styled(Link)<{ show: boolean }>`
  ${tokens.text('caption')}
  color: ${tokens.color.text_default};
  visibility: ${(p) => (p.show ? 'visible' : 'hidden')};
  flex-shrink: 0;
`;

const StyledPlanBadge = styled(PlanBadge)`
  margin-left: ${tokens.spacer.x1};
`;

export interface AccentColorSectionProps {
  color?: string;
  showReset: boolean;
  onColorChange: (color: string) => void;
  onColorReset: () => void;
  upsellPlan: FeatureGroup | null;
  onShowUpsell: () => void;
  hasRequiredPlan: boolean | null;
}

export function AccentColorSection(props: AccentColorSectionProps): JSX.Element | null {
  const {
    color = '',
    onColorChange,
    onColorReset,
    showReset,
    hasRequiredPlan,
    onShowUpsell,
    upsellPlan,
  } = props;
  const { t } = useTranslation('feedback');
  const handleColorChange = (newColor: string, isValid: boolean) => {
    if (isValid) {
      onColorChange(newColor);
    }
  };

  const triggerUpsellIfToggleIsNotPresent = () => {
    if (!hasRequiredPlan) {
      onShowUpsell();
    }
  };

  return (
    <Wrapper>
      <WizardLabel htmlFor="accent-color">
        {t('feedback-wizard.appearance.accent-color.label')}
        {!hasRequiredPlan && upsellPlan && (
          <StyledPlanBadge kind={upsellPlan} size="s" onClick={onShowUpsell} />
        )}
      </WizardLabel>
      <Caption>{t('feedback-wizard.appearance.accent-color.description')}</Caption>
      <PickerWrapper>
        <div>
          <ColorPicker
            isReadonly={!hasRequiredPlan}
            id="accent-color"
            value={color}
            onChange={handleColorChange}
            onClick={() => triggerUpsellIfToggleIsNotPresent()}
          />
        </div>
        <PickerAction>
          <ResetButton
            as="button"
            type="button"
            kind="inline"
            onClick={onColorReset}
            show={showReset}
          >
            {t('feedback-wizard.appearance.accent-color.reset')}
          </ResetButton>
        </PickerAction>
      </PickerWrapper>
    </Wrapper>
  );
}
